/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

/**
 * @description
 * Model that defines a fine type entity, including state handlers for modal management.
 *
 * @description
 * Base model that defines a **Fine Type** entity.
 * Includes properties for modal state management and methods
 * to handle CRUD modal interactions in the UI.
 *
 * This model is designed to be used both as an initial state
 * and as a helper utility to manage modal visibility and validation.
 *
 * @constant
 * @type {{DELETE: string, CREATE: string, EDIT: string}}
 */

export const MODAL_NAMES = {
    CREATE: 'isCreateModal',
    EDIT: 'isEditModal',
    DELETE: 'isDeleteModal',
};

/**
 * @description
 * Generic function to open or close any modal in a given state object.
 * Receives the modal name (from MODAL_NAMES), the state setter,
 * and a boolean indicating whether to open or close it.
 *
 * @example
 * toggleModal(MODAL_NAMES.CREATE, setFineType, true);  // open create modal
 * toggleModal(MODAL_NAMES.EDIT, setFineType, false);   // close edit modal
 *
 * @param {string} modalName - The modal property name (use from MODAL_NAMES).
 * @param {Function} setState - React state setter function.
 * @param {boolean} [open=true] - Whether to open (true) or close (false) the modal.
 * @returns {void}
 */
export const toggleFinesModal = (modalName, setState, open = true) => {
    setState(prev => {
        const updated = { ...prev, [modalName]: open };

        // Extra logic for specific modal types
        if (!open && (modalName === MODAL_NAMES.DELETE)) {
            updated.selectForDelete = [];
        }

        // Ensure mutual exclusivity between CREATE and EDIT modals
        if (open && modalName === MODAL_NAMES.CREATE) {
            updated.isEditModal = false;
        } else if (open && modalName === MODAL_NAMES.EDIT) {
            updated.isCreateModal = false;
        }

        return updated;
    });
};

export const FINE_TYPES_MODEL = {
    id: null,
    name: null,
    isEditModal: false,
    isCreateModal: false,
    selectForDelete: [],
    isDeleteModal: false,

    /**
     * Checks if the model is valid.
     * @returns {boolean} True if name is not empty or null.
     */
    isValid: function () {
        return this.name !== null && this.name.trim() !== '';
    },

    /**
     * Prepares the object for creation or edition.
     * @returns {{id: *, name: *}} Object formatted for add/edit operation.
     */
    toAddEdit: function () {
        return {
            id: this.id,
            name: this.name,
        };
    },

};
